import React from 'react';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { FiltersDialog } from 'core';
import { TDateFilter } from 'core/FiltersDialog/types';
import { compact, forEach, includes, isEmpty, keys, map, mapValues, pickBy, union, values, without } from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { selectCurrentMasterSettings, selectCurrentUser } from '@lib/selectors';

import { addQuery } from '@lib/services/queryService';

import { messageRefMapping } from 'deprecated/fragments';

import getCleanFilterComp from 'components/FixedFilters/helpers/getCleanFilterComp';
import getCleanFilterGraphQL from 'components/FixedFilters/helpers/getCleanFilterGraphQL';
import { BoxContentWithFilters } from 'components/v2';
import useURLSearchParams from 'hooks/useURLSearchParams';
import usePermissions from 'hooks/usePermissions';

import {
  EnumMessageChannel,
  EnumMessageRef,
  EnumMessageStatus,
  EnumMessageType,
  EnumMessageRecipientType,
} from './__generated__/QueryTableMessagesQuery.graphql';
import QueryTableMessages, { QueryTableMessagesProps } from './QueryTableMessages';
import QueryTextFieldSearchPatient from './QueryTextFieldSearchPatient';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    marginLeft: {
      marginLeft: unit * 2,
    },
  });

type TDefaultFilters = {
  messageType: string[];
  messageStatus: string[];
  dateRange: {
    selectedDate: string;
    fromDate: number;
    toDate: number;
  };
  messageRef: string[];
  messageChannel: string[];
  facility?: string[];
};

const defaultFilters: TDefaultFilters = {
  messageType: ['all'],
  messageStatus: ['all'],
  dateRange: {
    selectedDate: 'pastWeek',
    fromDate: moment()
      .subtract(7, 'days')
      .startOf('day')
      .valueOf(),
    toDate: moment()
      .endOf('day')
      .valueOf(),
  },
  messageRef: ['all'],
  messageChannel: ['all'],
  facility: ['all'],
};

type TSearchParams = {
  facility?: string;
  type?: string;
  status?: string;
  selectedDate?: string;
  fromDate?: string;
  toDate?: string;
  ref?: string;
  channel?: string;
  patient?: string;
  patientSearch?: string;
};

type TFilters = {
  messageRef: string[];
  messageStatus: string[];
  dateRange: TDateFilter;
  messageType: string[];
  messageChannel: string[];
  facility?: string[];
};

const searchParamsToQueryVariables = (
  searchParams: TSearchParams,
  isAdminOrManager: boolean,
  currentUserId: string,
): QueryTableMessagesProps['variables'] => {
  const {
    status: initialStatus,
    fromDate,
    toDate,
    channel,
    type: initialType,
    ref: initialRef,
    patient,
    patientSearch,
  } = searchParams;

  // Sanitize `Message.status` filter
  let status: EnumMessageStatus[] = [];
  if (initialStatus) {
    let initialStatusArray = initialStatus.split(',');
    if (initialStatusArray.includes('all')) {
      initialStatusArray =
        initialStatusArray.length === 1 ? [] : without(['delivered', 'sent', 'undelivered'], ...initialStatusArray);
    }
    if (initialStatusArray.includes('sent')) {
      status = union(status, ['sent', 'pending']);
    }
    if (initialStatusArray.includes('delivered')) {
      status = union(status, ['delivered', 'opened']);
    }
    if (initialStatusArray.includes('undelivered')) {
      status = union(status, ['undelivered']);
    }
  }
  if (isEmpty(status)) {
    // For better DB indexing, status must be sent
    // NOTE: skipped status also exists but we don't want to show it
    status = ['delivered', 'opened', 'pending', 'sent', 'undelivered'];
  }

  // Sanitize `Message.ref` filter
  // We don't want to display any item that have an Audit ref
  let ref = 'all,chatAudit,chatAuditTz';
  if (initialRef) {
    let refArray: EnumMessageRef[] = [];
    const allRefOptions = without(compact(values(messageRefMapping)), '???');
    forEach(allRefOptions, (option) => {
      const regexp = new RegExp(`^${option}|,${option}`);
      if (regexp.test(initialRef)) {
        const newRefs = keys(pickBy(messageRefMapping, (s) => s === option));
        refArray = union(refArray, newRefs as EnumMessageRef[]);
      }
    });
    ref = refArray.join(',');
    // Handle exclusions and omit `chatAudit` refs
    if (isEmpty(refArray) || includes(initialRef.split(','), 'all')) {
      ref = `all,chatAudit,chatAuditTz,${ref}`;
    }
  }

  let type = initialType;
  let recipientType;
  if (initialType?.includes('sendToProvider') || initialType?.includes('sendFromProvider')) {
    const hasOutbound = initialType?.includes('outbound');
    const hasInbound = initialType?.includes('inbound');
    type = initialType
      .replace('sendToProvider', !hasOutbound ? 'outbound' : '')
      .replace('sendFromProvider', !hasInbound ? 'inbound' : '');
    recipientType = initialType?.includes('all') ? 'all,doctor,staff' : 'doctor,staff';
  }

  return {
    filter: {
      _operators: {
        createdAt:
          fromDate && toDate
            ? { gte: moment(parseInt(fromDate, 10)).valueOf(), lte: moment(parseInt(toDate, 10)).valueOf() }
            : { gte: defaultFilters.dateRange.fromDate, lte: defaultFilters.dateRange.toDate },
        type: getCleanFilterGraphQL<EnumMessageType>(type),
        status: getCleanFilterGraphQL(status),
        ref: getCleanFilterGraphQL<EnumMessageRef>(ref),
        channel: getCleanFilterGraphQL<EnumMessageChannel>(channel),
        patient: getCleanFilterGraphQL(patient),
        recipientType: getCleanFilterGraphQL<EnumMessageRecipientType>(recipientType),
      },
      patient: isEmpty(patient) && patientSearch ? null : undefined,
    },
  };
};

const searchParamsToFilters = (searchParams: TSearchParams, isToUseSendAndReceiverFilters: boolean) => {
  const { type, status, selectedDate, fromDate, toDate, ref, channel, facility } = searchParams;
  return {
    messageType: type ? getCleanFilterComp(type) : defaultFilters.messageType,
    messageStatus: status ? getCleanFilterComp(status) : defaultFilters.messageStatus,
    dateRange: {
      selectedDate: selectedDate || defaultFilters.dateRange.selectedDate,
      fromDate: fromDate ? moment(parseInt(fromDate, 10)).valueOf() : defaultFilters.dateRange.fromDate,
      toDate: toDate ? moment(parseInt(toDate, 10)).valueOf() : defaultFilters.dateRange.toDate,
    },
    messageRef: ref ? getCleanFilterComp(ref) : defaultFilters.messageRef,
    messageChannel: channel ? getCleanFilterComp(channel) : defaultFilters.messageChannel,
    ...(isToUseSendAndReceiverFilters && {
      facility: facility ? getCleanFilterComp(facility) : defaultFilters.facility,
    }),
  };
};

const filtersToSearchParams = (filters: TFilters): TSearchParams => ({
  type: filters.messageType.join(','),
  status: filters.messageStatus.join(','),
  ...mapValues(filters.dateRange, (v) => `${v}`),
  ref: filters.messageRef.join(','),
  channel: filters.messageChannel.join(','),
  ...(filters.facility && { facility: filters.facility.join(',') }),
});

export interface WrapperQueryTableMessagesProps
  extends Omit<QueryTableMessagesProps, 'classes' | 'fragmentRef'>,
    WithStyles<typeof styles> {}

const WrapperQueryTableMessages: React.FC<WrapperQueryTableMessagesProps> = ({ classes, ...rest }) => {
  const { permissions } = usePermissions();
  const isAdmin = permissions?.isAdmin;
  const isManager = permissions?.isManager;
  const currentUser = useSelector(selectCurrentUser);

  const settings = useSelector(selectCurrentMasterSettings);
  const isToUseSendAndReceiverFilters = settings?.communication?.filterMessagesBySenderAndReceiver;
  if (!isToUseSendAndReceiverFilters) {
    delete defaultFilters.facility;
  }

  const searchParams = useURLSearchParams();
  const initialFilters = { ...searchParamsToFilters(searchParams, isToUseSendAndReceiverFilters) };
  const queryVariables = searchParamsToQueryVariables(
    searchParams,
    isAdmin || isManager || false,
    currentUser?.id as string,
  );

  return (
    <BoxContentWithFilters
      toolbarContent={
        <>
          <QueryTextFieldSearchPatient
            fullWidth
            autoFocus
            value={searchParams.patientSearch}
            onChange={(patientSearch, patients) => addQuery({ patient: map(patients, '_id').join(','), patientSearch })}
          />
          <div className={classes.marginLeft}>
            <FiltersDialog // actually a button
              filterName="messagingActivityMessages"
              isLocked={false}
              initialFilters={initialFilters}
              resetFiltersShape={defaultFilters}
              onApplyFilters={(filters) => addQuery(filtersToSearchParams(filters))}
              hideLockButton
            />
          </div>
        </>
      }
      content={<QueryTableMessages variables={queryVariables} stickyHeader {...rest} />}
    />
  );
};

export default withStyles(styles)(WrapperQueryTableMessages);
